import React, { useState } from 'react';
import axios from 'axios';
import { X, Download, CheckCircle2, Minimize2, ChevronUp, Heart } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import '../styles/exit-popup.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NAOExitPopup = ({ isOpen, onClose, onSubmit }) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  if (!isOpen) return null;

  const isWorkEmail = (email) => {
    const freeEmailProviders = [
      'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 
      'aol.com', 'icloud.com', 'mail.com', 'protonmail.com',
      'yandex.com', 'zoho.com', 'gmx.com', 'live.com', 'msn.com'
    ];
    
    const domain = email.split('@')[1]?.toLowerCase();
    return domain && !freeEmailProviders.includes(domain);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email || !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    if (!isWorkEmail(email)) {
      setError('Please use your work email address');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/peo-guide-download`, {
        email: email,
        source: 'nao_exit_popup',
        guide_type: 'nonprofit_whitepaper'
      });
      
      onSubmit(email);
    } catch (error) {
      // Continue with download even if tracking fails
    }
    
    setTimeout(() => {
      const pdfUrl = process.env.REACT_APP_NONPROFIT_GUIDE_PDF || 'https://customer-assets.emergentagent.com/job_2bac5358-3276-4771-b6d1-4470e68b545a/artifacts/f1hxm8az_How%20PEOs%20Support%20Nonprofits%20%282026%29.pdf';
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = 'How-PEOs-Support-Nonprofits-2026.pdf';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setIsSubmitting(false);
      setIsSuccess(true);
      
      setTimeout(() => {
        onClose();
        setIsSuccess(false);
        setEmail('');
      }, 2000);
    }, 500);
  };

  if (isMinimized) {
    return (
      <div className="exit-popup-minimized nao-minimized" onClick={() => setIsMinimized(false)}>
        <Heart size={18} />
        <span>Get Free Nonprofit PEO Guide</span>
        <ChevronUp size={18} />
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="exit-popup-overlay" onClick={onClose}>
        <div className="exit-popup-content exit-popup-success nao-popup" onClick={(e) => e.stopPropagation()}>
          <div className="success-icon nao-success-icon">
            <CheckCircle2 size={64} />
          </div>
          <h2 className="success-title">Download Starting...</h2>
          <p className="success-message">
            Your Nonprofit PEO Strategic Guide is downloading. Check your downloads folder!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="exit-popup-overlay" onClick={onClose}>
      <div className="exit-popup-content nao-popup" onClick={(e) => e.stopPropagation()}>
        <div className="exit-popup-actions">
          <button 
            className="exit-popup-minimize" 
            onClick={() => setIsMinimized(true)}
            title="Minimize"
          >
            <Minimize2 size={20} />
          </button>
          <button className="exit-popup-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>
        
        <div className="exit-popup-header">
          <div className="exit-popup-icon nao-popup-icon">
            <Heart size={48} />
          </div>
          <h2 className="exit-popup-title">Wait! A Resource Just for Nonprofits</h2>
          <p className="exit-popup-subtitle">
            Download our strategic guide: <strong>How Professional Employer Organizations Support Nonprofit Success</strong>
          </p>
        </div>

        <div className="exit-popup-benefits nao-benefits">
          <div className="exit-popup-benefit">
            ✓ For Nonprofit Leaders & Board Members
          </div>
          <div className="exit-popup-benefit">
            ✓ Strategic HR Partnership Insights
          </div>
          <div className="exit-popup-benefit">
            ✓ Mission-Focused Cost Savings
          </div>
        </div>

        <form onSubmit={handleSubmit} className="exit-popup-form">
          <Input
            type="email"
            name="email"
            placeholder="Enter your work email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError('');
            }}
            autoComplete="email"
            className="exit-popup-input"
            required
          />
          {error && <div className="exit-popup-error">{error}</div>}
          <Button type="submit" className="exit-popup-button nao-button" disabled={isSubmitting}>
            {isSubmitting ? 'Sending...' : 'Download Free Guide'}
          </Button>
        </form>

        <p className="exit-popup-privacy">
          No spam. Unsubscribe anytime. We respect your privacy.
        </p>
      </div>
    </div>
  );
};

export default NAOExitPopup;
